/**
 * /me/saved-searches — buyer's saved-search list with per-row alert toggle.
 *
 * Page renders saved-search rows the user has created on /marketplace via
 * the "Save this search" CTA. Each row shows the filter set, an alerts
 * toggle, and a delete button. When alerts are on, the user gets a daily
 * email digest of new listings that match.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { TrashIcon as Trash, BellRingingIcon as BellRinging, ArrowRightIcon as ArrowRight, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";

export default function SavedSearches() {
  const [rows, setRows] = useState(null);

  useEffect(() => { reload(); }, []);

  async function reload() {
    try {
      const { data } = await api.get("/me/saved-searches");
      setRows(data?.saved_searches || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }

  async function toggleAlerts(s, next) {
    try {
      await api.patch(`/me/saved-searches/${s.id}`, { alerts_enabled: next });
      setRows((rs) => rs.map((r) => (r.id === s.id ? { ...r, alerts_enabled: next } : r)));
      toast.success(next ? "Daily alerts on" : "Alerts paused");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(s) {
    if (!window.confirm(`Delete "${s.label}"? You won't get alerts for it any more.`)) return;
    try {
      await api.delete(`/me/saved-searches/${s.id}`);
      setRows((rs) => rs.filter((r) => r.id !== s.id));
      toast.success("Saved search deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="saved-searches-page">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-gold mb-2">My account</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Saved searches</h1>
        <p className="text-[#3F3F3F] mt-2 leading-relaxed max-w-2xl">
          We&rsquo;ll email you once a day when new listings match a search you&rsquo;ve saved.
          Pause any search anytime — your filter stays saved.
        </p>

        <div className="mt-8 space-y-3">
          {rows === null ? (
            <>
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </>
          ) : rows.length === 0 ? (
            <EmptyState />
          ) : (
            rows.map((s) => (
              <SavedSearchCard
                key={s.id}
                row={s}
                onToggle={(next) => toggleAlerts(s, next)}
                onDelete={() => remove(s)}
              />
            ))
          )}
        </div>
      </div>
    </AppShell>
  );
}

function EmptyState() {
  return (
    <div className="bg-cream/30 border border-[#E2DAC2] rounded-2xl p-8 text-center">
      <MagnifyingGlass size={36} weight="duotone" className="text-gold mx-auto mb-3" />
      <h2 className="font-spectral text-2xl mb-2">No saved searches yet</h2>
      <p className="text-[13px] text-[#6B6B6B] mb-5 max-w-md mx-auto leading-relaxed">
        Run a search on the marketplace, then tap <strong>Save this search</strong> at the top
        of the results. We&rsquo;ll email you when new listings match.
      </p>
      <Link
        to="/marketplace"
        className="inline-flex items-center gap-2 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 font-bold transition-colors"
        data-testid="saved-searches-empty-cta"
      >
        Browse the marketplace <ArrowRight size={13} weight="bold" />
      </Link>
    </div>
  );
}

function SavedSearchCard({ row, onToggle, onDelete }) {
  const params = new URLSearchParams();
  if (row.breed) params.set("breed", row.breed);
  if (row.sex) params.set("sex", row.sex);
  if (row.state) params.set("state", row.state);
  if (row.max_price) params.set("max_price", String(row.max_price));
  if (row.min_price) params.set("min_price", String(row.min_price));
  const summaryBits = [
    row.breed,
    row.sex && (row.sex === "male" ? "Male" : "Female"),
    row.state,
    row.max_price ? `≤ $${Number(row.max_price).toLocaleString()}` : null,
    row.min_price ? `≥ $${Number(row.min_price).toLocaleString()}` : null,
  ].filter(Boolean);
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-2xl p-5 flex items-start gap-4"
      data-testid={`saved-search-row-${row.id}`}
    >
      <div className="flex-1 min-w-0">
        <Link
          to={`/marketplace?${params.toString()}`}
          className="font-spectral text-lg leading-tight hover:text-crimson"
          data-testid={`saved-search-open-${row.id}`}
        >
          {row.label}
        </Link>
        <p className="text-[12.5px] text-[#6B6B6B] mt-1 leading-snug">
          {summaryBits.length > 0 ? summaryBits.join(" · ") : "All listings"}
        </p>
        {row.last_alert_match_count > 0 && (
          <p className="text-[11px] text-[#3DA56E] mt-1.5">
            <BellRinging size={11} weight="fill" className="inline mr-1" />
            Last alert: {row.last_alert_match_count} new {row.last_alert_match_count === 1 ? "match" : "matches"}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex flex-col items-end gap-1">
          <Switch
            checked={!!row.alerts_enabled}
            onCheckedChange={onToggle}
            data-testid={`saved-search-alerts-${row.id}`}
          />
          <span className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">
            {row.alerts_enabled ? "Alerts on" : "Paused"}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          data-testid={`saved-search-delete-${row.id}`}
          className="text-[#9A9A9A] hover:text-crimson"
          aria-label="Delete saved search"
        >
          <Trash size={16} weight="regular" />
        </Button>
      </div>
    </div>
  );
}
